import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

// material-ui
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import EditOutlined from '@ant-design/icons/EditOutlined';
import RetweetOutlined from '@ant-design/icons/RetweetOutlined';

import { PopupTransition } from 'components/@extended/Transitions';
import { updateSession } from 'services/api/sessions';
import { MIN_SESSION_MINUTES, MAX_SESSION_MINUTES, isValidSessionDuration, parseDurationInput } from 'utils/sessionDuration';

// Local-time formatters. The scheduled_* columns are TIMESTAMP WITHOUT TIME
// ZONE, so sending a UTC ISO string would shift every session by the offset.
const toLocalDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const toLocalISOStr = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

/**
 * Build a Date from the session's own date + start time strings rather than
 * from scheduled_start, so it lands in local time regardless of how the row
 * was serialized.
 */
const startDateTimeOf = (session) => {
  if (!session) return new Date();
  const [y, m, d] = (session.date || session.sessionDate || '').split('-').map(Number);
  const [hh, mm] = (session.startTime || '00:00').split(':').map(Number);
  if (!y || !m || !d) return new Date();
  return new Date(y, m - 1, d, hh || 0, mm || 0, 0, 0);
};

/**
 * Outlook-style session editor.
 *
 * A one-off session edits as itself. A session that belongs to a recurring
 * series (non-null seriesId) first asks what the change applies to:
 *
 *   • This session          — only this occurrence, date included.
 *   • This and following    — this one plus every later occurrence.
 *   • Entire series         — every occurrence in the series.
 *
 * The date field is disabled for the two series scopes: each occurrence owns
 * its own date, and the backend deliberately never copies one across. A time
 * change does carry across, as time-of-day + duration.
 */
export default function EditSessionDialog({
  open,
  session,
  scope = 'single',
  services = [],
  therapists = [],
  allowTherapistChange = false,
  onClose,
  onSaved
}) {
  const isSeries = Boolean(session?.seriesId) && scope !== 'single';

  const [startDateTime, setStartDateTime] = useState(() => startDateTimeOf(session));
  const [duration, setDuration] = useState(session?.duration || 60);
  const [serviceId, setServiceId] = useState('');
  const [therapistId, setTherapistId] = useState('');
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // Reset the form each time the dialog is opened on a session. Without this
  // the second open would still show the previous session's values.
  useEffect(() => {
    if (!open || !session) return;
    setStartDateTime(startDateTimeOf(session));
    setDuration(session.duration || 60);
    setServiceId(session._raw?.service_id || session._raw?.service?.id || '');
    setTherapistId(session.therapistId || '');
    setNotes(session.notes || '');
    setError(null);
  }, [open, session]);

  const original = useMemo(
    () => ({
      start: startDateTimeOf(session),
      duration: session?.duration || 60,
      serviceId: session?._raw?.service_id || session?._raw?.service?.id || '',
      therapistId: session?.therapistId || '',
      notes: session?.notes || ''
    }),
    [session]
  );

  const isDurationValid = isValidSessionDuration(duration);

  const dateChanged = startDateTime && toLocalDateStr(startDateTime) !== toLocalDateStr(original.start);
  const isDirty =
    dateChanged ||
    (startDateTime && startDateTime.getHours() !== original.start.getHours()) ||
    (startDateTime && startDateTime.getMinutes() !== original.start.getMinutes()) ||
    duration !== original.duration ||
    serviceId !== original.serviceId ||
    (allowTherapistChange && therapistId !== original.therapistId) ||
    (notes || '') !== (original.notes || '');

  const handleSave = async () => {
    if (!session || !startDateTime || isNaN(startDateTime)) return;
    if (!isDurationValid) return;
    setIsSaving(true);
    setError(null);

    try {
      const end = new Date(startDateTime.getTime() + duration * 60000);
      const payload = {
        scheduled_start: toLocalISOStr(startDateTime),
        scheduled_end: toLocalISOStr(end),
        service_id: serviceId || null,
        notes: notes || null
      };
      // The date belongs to the occurrence, so it only travels on a single
      // edit — the backend ignores it for series scopes anyway.
      if (scope === 'single') {
        payload.session_date = toLocalDateStr(startDateTime);
      }
      if (allowTherapistChange && therapistId) {
        payload.therapist_id = therapistId;
      }

      const res = await updateSession(session.id, payload, { scope });
      const count = res?.series_updated_count || 1;
      if (scope === 'single') {
        onSaved?.('Session updated', 'success');
      } else if (count === 1) {
        // A series edit that reached nothing else. Saying "Session updated"
        // here reads as success and hides the reason, so name it: every other
        // occurrence was already in the past, started, or carrying data.
        onSaved?.('Updated this session only — no later sessions in the series were eligible to change', 'warning');
      } else {
        onSaved?.(`Updated ${count} sessions in this series`, 'success');
      }
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Update failed';
      setError(typeof detail === 'string' ? detail : 'Update failed');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !isSaving && onClose?.()} maxWidth="sm" fullWidth slots={{ transition: PopupTransition }}>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <EditOutlined />
          <Typography variant="h5">{isSeries ? 'Edit Recurring Session' : 'Edit Session'}</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            {/* The scope came from the dropdown that opened this dialog, so
                it is stated here rather than asked again. */}
            {isSeries && (
              <>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <RetweetOutlined style={{ marginTop: 3 }} />
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {scope === 'following' ? 'This and following sessions' : 'Entire series'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {session.recurrenceRule ? `Recurring — ${session.recurrenceRule}` : 'Part of a recurring series'}
                    </Typography>
                  </Box>
                </Stack>
                <Alert severity="info" sx={{ py: 0.5 }}>
                  <Typography variant="caption">
                    Each occurrence keeps its own date — the new time is applied to every date in scope. Sessions that are in the past,
                    already started, or already have data collected are left untouched.
                  </Typography>
                </Alert>
                <Divider />
              </>
            )}

            {/* A single edit can move the session to another day. A series edit
                can only change the time: each occurrence keeps its own date, so
                offering a date field there would be offering something the
                backend deliberately won't do. */}
            {scope === 'single' ? (
              <DateTimePicker
                label="Start date & time"
                value={startDateTime}
                onChange={(v) => setStartDateTime(v)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            ) : (
              <TimePicker
                label="Start time"
                value={startDateTime}
                onChange={(v) => setStartDateTime(v)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: 'Applied to every occurrence in scope, each on its own date.'
                  }
                }}
              />
            )}

            <TextField
              fullWidth
              label="Duration (minutes)"
              type="number"
              value={duration}
              onChange={(e) => setDuration(parseDurationInput(e.target.value))}
              error={!isDurationValid}
              helperText={isDurationValid ? '' : `Duration must be between ${MIN_SESSION_MINUTES} and ${MAX_SESSION_MINUTES} minutes.`}
              slotProps={{ htmlInput: { min: MIN_SESSION_MINUTES, max: MAX_SESSION_MINUTES, step: 15 } }}
            />

            {allowTherapistChange && therapists.length > 0 && (
              <FormControl fullWidth>
                <InputLabel>Therapist</InputLabel>
                <Select value={therapistId} label="Therapist" onChange={(e) => setTherapistId(e.target.value)}>
                  {therapists.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {services.length > 0 && (
              <FormControl fullWidth>
                <InputLabel>Program</InputLabel>
                <Select value={serviceId} label="Program" onChange={(e) => setServiceId(e.target.value)}>
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {services.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField fullWidth multiline rows={3} label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />

            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </LocalizationProvider>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button color="secondary" onClick={() => onClose?.()} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving || !isDirty || !isDurationValid}
          startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <EditOutlined />}
        >
          {isSaving ? 'Saving…' : scope === 'single' ? 'Save Session' : 'Save Series'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

EditSessionDialog.propTypes = {
  open: PropTypes.bool,
  session: PropTypes.object,
  scope: PropTypes.oneOf(['single', 'following', 'series']),
  services: PropTypes.array,
  therapists: PropTypes.array,
  allowTherapistChange: PropTypes.bool,
  onClose: PropTypes.func,
  onSaved: PropTypes.func
};
